import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Menu, X } from "lucide-react";
import { useState } from "react";

export function Nav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/course", label: "The Course" },
    { to: "/about", label: "About" },
    { to: "/testimonials", label: "Voices" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-[#FAFAFA]/95 backdrop-blur border-b border-black/5">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-10 py-5">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <span className="w-8 h-[1px] bg-[#D4AF37]" />
          <span className="font-serif-display text-base tracking-[0.3em] uppercase">Lata Illustrator</span>
        </Link>
        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`overline link-underline ${loc.pathname === l.to ? 'text-black' : ''}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-4">
          {user ? (
            <>
              <Link
                data-testid="nav-dashboard"
                to={user.role === 'admin' ? '/admin' : '/dashboard'}
                className="overline link-underline"
              >
                {user.role === 'admin' ? 'Admin' : 'Dashboard'}
              </Link>
              {user.role === 'admin' && (
                <Link
                  data-testid="nav-preview-student"
                  to="/dashboard"
                  className="overline link-underline text-[#D4AF37]"
                >
                  Preview as Student
                </Link>
              )}
              <button
                data-testid="nav-logout"
                onClick={() => { logout(); nav('/'); }}
                className="overline link-underline"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link data-testid="nav-login" to="/login" className="overline link-underline">Sign in</Link>
              <Link data-testid="nav-enroll" to="/course" className="btn-editorial">Enroll</Link>
            </>
          )}
        </div>
        <button data-testid="nav-menu-toggle" className="md:hidden" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-black/5 bg-[#FAFAFA] px-6 py-6 space-y-5">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-m-${l.label.toLowerCase().replace(/\s/g, '-')}`}
              onClick={() => setOpen(false)}
              className="block overline"
            >
              {l.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link
                onClick={() => setOpen(false)}
                to={user.role === 'admin' ? '/admin' : '/dashboard'}
                className="block overline"
                data-testid="nav-m-dashboard"
              >
                {user.role === 'admin' ? 'Admin' : 'Dashboard'}
              </Link>
              {user.role === 'admin' && (
                <Link
                  onClick={() => setOpen(false)}
                  to="/dashboard"
                  className="block overline text-[#D4AF37]"
                  data-testid="nav-m-preview-student"
                >
                  Preview as Student
                </Link>
              )}
              <button
                data-testid="nav-m-logout"
                onClick={() => { logout(); setOpen(false); nav('/'); }}
                className="block overline"
              >Sign out</button>
            </>
          ) : (
            <>
              <Link data-testid="nav-m-login" to="/login" className="block overline" onClick={() => setOpen(false)}>Sign in</Link>
              <Link data-testid="nav-m-enroll" to="/course" className="btn-editorial w-full" onClick={() => setOpen(false)}>Enroll</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-black/10 mt-32 py-16 bg-[#F2F0EB]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <p className="overline mb-4">Lata Illustrator</p>
          <p className="font-serif-display text-2xl leading-tight max-w-xs">
            An editorial studio for the language of Indian silhouettes.
          </p>
        </div>
        <div className="md:col-span-3">
          <p className="overline mb-4">Studio</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/course" className="link-underline">The Course</Link></li>
            <li><Link to="/about" className="link-underline">About Lata</Link></li>
            <li><Link to="/testimonials" className="link-underline">Voices</Link></li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <p className="overline mb-4">Correspond</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/contact" className="link-underline">Contact</Link></li>
            <li><Link to="/faq" className="link-underline">FAQ</Link></li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-12 pt-8 border-t border-black/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <p className="text-xs text-[#6B6B6B]">© {new Date().getFullYear()} Lata Illustrator. All rights reserved.</p>
        <p className="overline">Handcrafted in India</p>
      </div>
    </footer>
  );
}
